'use client';

import {useState, useEffect, useContext, useRef, Fragment} from 'react';
import { useDebouncedEffect } from '@/hooks/use-debounced-effect';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { appendParamsToURL } from '@/lib/dataHandlers/URLBuilders';
import DataContext from '@/contexts/DataContext';
import { fetchViewParams, extractDefaultParams } from '@/lib/dataHandlers/fetch-view-params';
import { fetchAlertsForProject } from '@/lib/dataHandlers/fetch-alerts';
import { normalizeSearch, extractURLParams, validateProject, validateViewParamValues } from '@/lib/validate/validation-helpers';
import { useComponentLogger } from '@/lib/component-logger';
import { syncLastUsedParams } from '@/lib/dataHandlers/sync-last-used-params';
import { escapeQuotes, cleanJSON } from '@/lib/dataHandlers/StringHelpers';
import _ from 'lodash';

/**
 * Validates params, removing them and redirecting to the longest valid url as needed
 * Only triggered when URL (location.search) changes
 * Also sets default view parameters as needed
 * @param {*} param0 
 * @returns 
 */
export function ValidateURLParams({ children }) {
  const logger = useComponentLogger("Validate URL Params");
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);
  const location = useLocation();
  const { appsAndViews, projects, setProjects, currentProject, setCurrentProject, alerts, setAlerts, setCurrentAlert, currentView, setCurrentView, viewParams, setViewParams, prelimParamCount, setPrelimParamCount, selectedViewParams, setSelectedViewParams, previousURL, setPreviousURL, externalURL, doneValidating, setDoneValidating } = useContext(DataContext);
  const requestedURL = JSON.parse(localStorage.getItem("requestedURL"));
  // const projects = JSON.parse(localStorage.getItem("projects"));
  // const appsAndViews = JSON.parse(localStorage.getItem("appsAndViews"));
  const [searchParams, setSearchParams] = useSearchParams();
  const [URLParams, setURLParams] = useState({});

  const appsAndViewsReady = appsAndViews !== null;
  const projectsReady = projects !== null;
  

  logger.debug('validate hit', location.pathname, requestedURL, appsAndViews);
  logger.debug("app change", appsAndViews)

  // make validations cancellable (e.g. if user clicks away, then cancel the validation so it doesn't interfere with the new url)
  const validationRunRef = useRef(0);

  const beginValidationRun = () => {
    validationRunRef.current += 1;
    return validationRunRef.current;
  };

  const isStaleRun = (runId) => runId !== validationRunRef.current;


  const processParams = async (runId, startPath) => {
    if (runId == null || startPath == null) {
      logger.error('processParams called without run ownership');
      return;
    }
    logger.debug('start validate', location.pathname, startPath);

    // cancel validation if stale or path changed
    if (isStaleRun(runId)) return;
    if (location.pathname !== startPath) return;

    let params = extractURLParams(searchParams, requestedURL);
    logger.debug('extract params', params)

    await validateParams(params, runId, startPath);
    if (isStaleRun(runId)) return;

    setIsChecking(false);
    return;
  };

  useDebouncedEffect(300, () => {   
    const runId = beginValidationRun();
    const startPath = location.pathname;

    async function validate(runId, startPath) {
      if (isStaleRun(runId)) return;
      if (location.pathname !== startPath) return;
      // if (!appsAndViews || appsAndViews.length === 0) return;

      setDoneValidating(false);
      // normalize prev and cur url and remove any slashes (e.g. sometimes prev starts with a '/' while cur doesn't)
      const prev = normalizeSearch(previousURL);
      const cur = normalizeSearch(location.search);


      logger.debug('location change', cur, 'prev url', prev, 'req url', requestedURL, prev !== cur, appsAndViews );

      // if (prev !== cur) {
        processParams(runId, startPath).catch(() => {
          setDoneValidating(false);
        });
      // } else setDoneValidating(true);
    }

    validate(runId, startPath);

  }, [location.search, location.pathname, projects, appsAndViews])


  // useEffect(() => {
  //   setDoneValidating(false);
  //   logger.debug('location change: initial pg load', location.search, previousURL, requestedURL)
  //   // if (location.search !== previousURL) {
  //     processParams().catch(() => {
  //       // noop
  //       // logger.debug('processed params', location.search, previousURL)
  //       setDoneValidating(false);
  //     });
  //   // } else setDoneValidating(true);

  // }, []);

 
  /**
   * Params follow a hierarchy (e.g. proj->app->view->view params)
   * Goes down the hierarchy and validates each param
   * If a param is invalid, redirects to the last valid "level" (e.g. if app is invalid, redirects to /proj instead of /proj&app)
   * Params are validated by checking if they're present in a list of valid options for that param
   * TODO: If a view is selected: after its params are validated, the last used values are added if present, then the default values are added to any remaining params that have no value
   * If a view is selected: after its params are validated, default values are added if needed (e.g.  param 'site' has a default val, but isn't in the url -> we add site:defaultVal  to the url)
   * @param {*} params 
   * @returns 
   */
  const validateParams = async (params, runId, startPath) => {
    logger.debug('validate start', isStaleRun(runId), currentView, appsAndViews, appsAndViewsReady, projectsReady);
    

    // let userProjects = [];
    // if (projects && projects.length > 0){
    //   userProjects = projects;
    // } else {
    //   userProjects = JSON.parse(localStorage.getItem('projects'));
    // }

    logger.debug('params to validate', params, location, previousURL, projects, appsAndViews);
    // set basepath to be the current path (e.g. / vs /alerts)
    let validatedURL = requestedURL?.pathname ? requestedURL.pathname : '/';
    let shouldRedirect = false;

    if (params.proj !== null && params.proj !== undefined) {
      if (!projectsReady) return;

      let validProject = validateProject(params.proj, projects)
      logger.debug('valid project', validProject, currentProject);

      // Start by validating the project, using a list of projects the user has access to
      if (validProject !== null && validProject !== undefined) {
        // each valid param is appended to the url string, which is used to redirect if we hit an invalid param
        validatedURL += `?proj=${params.proj}`
        // if (currentProject === null || validProject.id !== currentProject.id) setCurrentProject(validProject);
        if (validProject.id !== currentProject.id){
          logger.debug('valid project: setcurproj', validProject);
          setCurrentProject(validProject);
        } 

        // alertID provided
        if (location.pathname === '/alerts') {
          let lastUsedAlert = JSON.parse(localStorage.getItem(`lastUsedAlert-${currentProject?.id}`))
          let alertVal = params.alertID ?? lastUsedAlert?.value ?? null;
          logger.debug('alert to check', alertVal, lastUsedAlert)

          let validAlerts = await fetchAlertsForProject(params.proj);
          setAlerts(validAlerts);
          
          let validAlert = validAlerts?.find(
            (alert) => alert.value ===alertVal.replace('@', '')
          );
  
          logger.debug('valid alert', validAlert, validAlerts, params.alertID);
          if (validAlert) { 
            setCurrentAlert(validAlert);
            validatedURL += `&alertID=${lastUsedAlert.value}`;

            if (isStaleRun(runId)) return;
            if (location.pathname !== startPath) return;
            // syncLastUsedParams(`lastUsedAlert-${currentProject?.id}`, validParamObject.validParams);

            setPreviousURL(validatedURL.replace('/', ''));
            setDoneValidating(true);
            logger.debug('navigate: valid alert');
            // navigate(validatedURL, {replace: true});
            if (projectsReady && appsAndViewsReady) navigate(validatedURL, {replace: true});
          } else {
            setCurrentAlert(null);
            logger.debug('navigate: invalid alert');
            setPreviousURL(validatedURL.replace('/', ''));
            setDoneValidating(true);

            if (isStaleRun(runId)) return;
            if (location.pathname !== startPath) return;
            // navigate(validatedURL, {replace: true});
            if (projectsReady && appsAndViewsReady) navigate(validatedURL, {replace: true});
          }
          return;
           
        } else if ((params.appID !== null && params.appID !== undefined) && (params.viewID !== null && params.viewID !== undefined)) { // both app and view provided
        // } else if (params.hasOwnProperty('appID') && params.hasOwnProperty('viewID')) { // both app and view provided
          if (!appsAndViewsReady) {
            logger.debug('validate start deferred: appsAndViews not ready');
            return;
          }
         
          let validApp = validateApp(params.appID, appsAndViews);
          logger.debug('valid app', validApp, params.appID, appsAndViews)
          
          if (validApp !== null && validApp !== undefined) {
            let appArray = [];
            appArray.push(validApp);

            let validView = validateView(params.viewID, appArray);
            logger.debug('valid app view', validView);

            if (validView !== null && validView !== undefined) {
              validatedURL += `&appID=${params.appID}`; // apps only exist with a view
              validatedURL += `&viewID=${params.viewID}`;
              // if (!currentApp?.id !== validApp.id) {
              //   setCurrentApp(validApp);
              // }
              
              if (currentView?.id !== validView.id) {
                logger.debug('set view: valid app view');
                if (validView.viewData && typeof validView.viewData === "string") {
                  let parsed = cleanJSON(validView.viewData);
                  logger.debug('parsed view', parsed);
                  validView.viewData = parsed;
                }
                logger.debug('set selected: no view data');
                // if (!isStaleRun(runId)) setSelectedViewParams({});
                logger.debug('set view: app and view', validView);
                // setCurrentView(validView);
                if (!isStaleRun(runId)) setCurrentView(validView);
              }

              await fillMissingParams(validView, validatedURL, params.params || {}, validProject.id, previousURL, runId, startPath);
            } else {
              
              shouldRedirect = true;
             
              setPreviousURL(validatedURL.replace('/', ''));
              setDoneValidating(true);

              if (isStaleRun(runId)) return;
              if (location.pathname !== startPath) return;
              logger.debug('navigate: invalid view');
              // navigate(validatedURL, {replace: true});
              if (projectsReady && appsAndViewsReady) navigate(validatedURL, {replace: true});
              return;
            }
          } else {
            
            shouldRedirect = true;
           
            setPreviousURL(validatedURL.replace('/', ''))
            setDoneValidating(true);

            if (isStaleRun(runId)) return;
            if (location.pathname !== startPath) return;
            logger.debug('navigate: invalid app');
            // navigate(validatedURL, {replace: true});
            if (projectsReady && appsAndViewsReady) navigate(validatedURL, {replace: true});
            return;
          }

          // just view provided
        } else if (params.viewID !== null && params.viewID !== undefined) {
          // let validView = validateView(params.viewID, appsAndViews)
          if (!appsAndViewsReady) {
            logger.debug('validate start deferred: appsAndViews not ready');
            return;
          }

          let validView = validateView(params.viewID, appsAndViews);
          logger.debug('valid view', validView);
          if (validView !== null && validView !== undefined) {
            validatedURL += `&viewID=${params.viewID}`;
            
            // if (!currentApp?.id !== validApp.id) {
            //   setCurrentApp(validApp);
            // }

            if (currentView?.id !== validView.id) {
              logger.debug('set view: valid app view');
              if (validView.viewData && typeof validView.viewData === "string") {
                let parsed = cleanJSON(validView.viewData);
                logger.debug('parsed view', parsed);
                validView.viewData = parsed;
              }
              logger.debug('set selected: no view data')
              // if (!isStaleRun(runId)) setSelectedViewParams({})
              logger.debug('set view: only view', validView);
              // setCurrentView(validView);
              if (!isStaleRun(runId)) setCurrentView(validView);
            }

            await fillMissingParams(validView, validatedURL, params.params || {}, validProject.id, previousURL, runId, startPath);
          } else { 
            
            shouldRedirect = true;
            
            setPreviousURL(validatedURL.replace('/', ''));
            setDoneValidating(true);

            if (isStaleRun(runId)) return;
            if (location.pathname !== startPath) return;
            logger.debug('navigate: only proj');
            // navigate(validatedURL, {replace: true});
            if (projectsReady && appsAndViewsReady) navigate(validatedURL, {replace: true});
            return;
          }
        } else {
          
          shouldRedirect = true;
          
          setPreviousURL(validatedURL.replace('/', ''));
          setDoneValidating(true);

          if (isStaleRun(runId)) return;
          if (location.pathname !== startPath) return;
          logger.debug('navigate: only proj');
          // navigate(validatedURL, {replace: true});
          if (projectsReady && appsAndViewsReady) navigate(validatedURL, {replace: true});
          return;
        }
        
      } else {
        logger.debug("valid project: invalid", validatedURL, projectsReady, appsAndViewsReady);
        
        shouldRedirect = true;
        
        setPreviousURL(validatedURL.replace('/', ''));
        setDoneValidating(true);

        if (isStaleRun(runId)) return;
        if (location.pathname !== startPath) return;
        logger.debug('navigate: invalid proj');
        // navigate(validatedURL, {replace: true});
        if (projectsReady) navigate(validatedURL, {replace: true});
        return;
      }
    } else if (requestedURL.search.length > 0) {
      
      // if there's invalid params, reroute to 404
      setPreviousURL(validatedURL.replace('/', ''));
      setDoneValidating(true);

      if (isStaleRun(runId)) return;
      if (location.pathname !== startPath) return;
      logger.debug('navigate: invalid route');
      // navigate('/errors/not-found', {replace: true})
      if (projectsReady && appsAndViewsReady) navigate('/errors/not-found', {replace: true});
    } else {
      logger.debug('no params', requestedURL, validatedURL);
      setPreviousURL(validatedURL);
      setDoneValidating(true);
      // // navigate('/', {replace: true});
      // navigate('/');
      // return;
    }


  }

  const validateApp = (appID, validAppsAndViews) => {
    if (appID === null || appID === undefined) return null;
    let validApp = null;

    for (const app of validAppsAndViews) {
      if (validApp !== null) break;
      if (app.id.replace('@', '') === appID.replace('@', '')) {
        validApp = app;
        break;
      }
    }
    return validApp;
  }

  const validateView = (viewID, validAppsAndViews) => {
    // // logger.debug('validate view', viewID, validAppsAndViews);
    if (viewID === null || viewID === undefined) return null;
    if (!validAppsAndViews || validAppsAndViews.length === 0) return null;
    let validView = null;
    for (const app of validAppsAndViews) {
      if (validView !== null) break;
      for (const view of app.items) {
        // // logger.debug('cur view', view.id, viewID);
        if (view.id.replace('@', '') === viewID.replace('@', '')) {
          validView = view;
          break;
        }
      }

    }
    return validView;
  }

  /**
   * 
   * Returns a string of the valid params and null if no params are valid
   * @param {*} params 
   * @param {*} view 
   */
  const validateViewParams = async (params, view, projectName) => {
    // validate that each param is one of the params that a view accepts
    // e.g. if a view only accepts site and span, params can only be site and/or span
    let validParamTypes = await validateViewParamTypes(params, view);
    setURLParams(validParamTypes);
    logger.debug('valid params hit', params, view, validParamTypes, projectName);

    // fetch view params
    // logger.debug('fetch params: validate');
    // let paramObject = await fetchViewParams(view.viewData.parameters, projectName, validParamTypes);
    let paramObject = await fetchViewParams(view.viewData, projectName, validParamTypes);
  
    let paramData = paramObject.params;
    let prelimParamCount = paramObject.prelimParamCount;
    logger.debug('valid param data', paramObject);

    logger.debug('set params: validate view params', paramData);
    setViewParams(Object.values(paramData));
    setPrelimParamCount(prelimParamCount);

    // validate that each accepted param has a valid value
    // e.g. each param has a valid list of options, so check if the value belongs to that list
    let validParamObject = validateViewParamValues(validParamTypes, paramData);
    logger.debug('valid params final', validParamObject, validParamTypes, params);
    if (validParamObject !== null) {
      // return '{' + validParams + '}';
      return validParamObject;
    } else return null;
  }

  /**
   * Validate the input params against the view definitions'
   * Propogates the view param definition object properties (e.g. kind)
   * @param {*} urlParams 
   * @param {*} view 
   * @returns validParams, an object containing the valid param objects
   */
  const validateViewParamTypes = async (urlParams, view) => {
    logger.debug('start type', urlParams, view);
    // extract view param types
    let definedParams = {};
    for (const param of view.viewData.parameters) {
      // logger.debug('cur type', param)
      if (param.name) definedParams[param.name] = param;
      if (param.childParams) {
        for (const childParam of param.childParams) {
          if (childParam.name) definedParams[childParam.name] = childParam;
        }
      }
    }

    let validParams = {};
     for (const key of Object.keys(urlParams)) {
      if (definedParams[key]) {
        logger.debug('valid type', definedParams[key], urlParams[key])
        // account for potential diff formats of urlParams
        let val = null;
        if (Array.isArray(urlParams[key])) {
          val = urlParams[key];
        } else if (typeof urlParams[key] === "object") { // typeof can't distinguish between an array and an object, hence the need to check Array.isArray above
          val = urlParams[key].value;
        } else val = urlParams[key];
        validParams[key] = {...definedParams[key], value: val};
      }
    }

    return validParams;
  }

  const extractLastSelectedParams = async (view, validParams) => {
    const app = view.mobileAppRef;
    const lastUsedParams = await JSON.parse(localStorage.getItem(`lastUsedParams-${app.val}`));
    // let newParams = {...validParams};

    logger.debug('last selected start', lastUsedParams, validParams, view);

    if (!lastUsedParams || Object.keys(lastUsedParams).length === 0) {
      return validParams;
    }

    if (!validParams || Object.keys(validParams).length === 0) {
      // logger.debug('start default: no given params');
      return lastUsedParams;
    }

    let syncedParams = syncLastUsedParams(`lastUsedParams-${app.val}`, validParams);
    logger.debug('last selected sync', syncedParams, validParams)


    return syncedParams;
  }

  /**
   * Fetches and sets viewParams
   * Follows a pipeline to fill in any missing parameters using this hierarchy:
   * url params -> last used params -> default params
   * 
   * @param {*} view 
   * @param {*} validatedURL 
   * @param {Object} urlParams the params extracted from the url
   */
  const fillMissingParams = async (view, validatedURL, urlParams, projectName='', prevURL, runId, startPath) => {
    logger.debug('start default:', view, urlParams, selectedViewParams, projectName)
    const viewParams = view.viewData.parameters;
    const appID = view.mobileAppRef?.val;

    // logger.debug('start default:', viewParams, urlParams, selectedViewParams, projectName)

    // fill in any valid last selected params
    let lastUsedParams = await extractLastSelectedParams(view, urlParams);

    logger.debug('start default fetch lastUsed', lastUsedParams, urlParams);

    const validParamObject = await validateViewParams(lastUsedParams, view, projectName);
    let newParams = validParamObject?.validParams || {};

    logger.debug('start default valid', validParamObject, newParams);

    // fetch and set viewParams (which also fetches the default params)
    // let paramObject = await fetchViewParams(viewParams, projectName, newParams);
    let paramObject = await fetchViewParams(view.viewData, projectName, newParams);
    if (paramObject === null || paramObject === undefined) return;
    
    let paramData = paramObject.params;
    let prelimParamCount = paramObject.prelimParamCount;

    logger.debug('set params: validate render default', paramData);
    setViewParams(Object.values(paramData));
    setPrelimParamCount(prelimParamCount);

    // fill in any valid default params
    if (Object.keys(newParams).length !== prelimParamCount) {
      logger.debug('extract default start', paramData, newParams)
      let defaultParamObject = await extractDefaultParams(paramData, newParams);
      logger.debug('extract default result', defaultParamObject, newParams)
      if (defaultParamObject) {
        newParams = defaultParamObject;
      }
    }


    // update lastUsedParams and selectedParams with the new vals
    if (appID) syncLastUsedParams(`lastUsedParams-${appID}`, newParams);
    // logger.debug('set selected: default', newParams, lastUsedParams, selectedViewParams, _.isEqual(newParams, selectedViewParams));

    // if ( !_.isEqual(newParams, selectedViewParams)) setSelectedViewParams(newParams);
    // setSelectedViewParams(newParams);

    // update the url
    const newURL = appendParamsToURL(validatedURL, newParams);
    logger.debug('should set', newParams, lastUsedParams, selectedViewParams, _.isEqual(newParams, selectedViewParams), newURL === prevURL, newURL, prevURL)
    

    if ( !_.isEqual(newParams, selectedViewParams) || newURL !== prevURL) {
      logger.debug('set selected: default', newParams, lastUsedParams, selectedViewParams, _.isEqual(newParams, selectedViewParams), newURL === prevURL, newURL, prevURL);
      if (!isStaleRun(runId)) setSelectedViewParams(newParams);
      // setSelectedViewParams(newParams);
    }
    if (newURL !== prevURL) {
      if (isStaleRun(runId)) return;
      if (location.pathname !== startPath) return;
      logger.debug('navigate: add last used via validate', newParams, newURL, prevURL);
      // navigate(newURL, {replace: true});
      if (projectsReady && appsAndViewsReady) navigate(newURL, {replace: true});
    } 

    setPreviousURL(newURL);
    setDoneValidating(true);

  }

  return <Fragment>{children}</Fragment>;
}
